import { div, label as labelEl, span, type Mountable, type Renderable } from '@llui/dom';

/**
 * `<FormField>` — label + control + help/error chrome.
 *
 * Consolidates the auth-form-row, settings-section row, and new-
 * entity-modal label triplet. The chrome is:
 *
 *  - uppercase Inter caption label (the design-system caption
 *    treatment per 30 §2 + §3.4 secondary-text), `for` wired to the
 *    caller's `htmlFor`.
 *  - the control itself (children — usually an `<input>` / `<select>`
 *    / `<textarea>`).
 *  - optional help line below in muted Inter 13px.
 *  - optional error line with leading ⚠ glyph (non-color cue per
 *    the a11y commitment) wired via `aria-describedby` on the
 *    control's `id` so SR users hear the error after the label.
 *
 * The caller passes the actual input as `children` — we don't own
 * the input semantics (autocomplete, autofocus, aria-invalid, etc.)
 * because those vary per field. The atom does provide a stable id
 * pair for the help/error region the caller wires up:
 *
 *   formField({
 *     id: 'sign-in-email',
 *     label: t('auth.email'),
 *     help: t('auth.email_help'),
 *     children: [input({ id: 'sign-in-email', 'aria-describedby':
 *       'sign-in-email-help sign-in-email-error', ... })],
 *   })
 *
 * The error slot is always rendered (empty), so setting the slot's
 * text content from outside keeps the aria-describedby wiring stable
 * (no need to flip describedby when an error appears/disappears).
 */

export interface FormFieldOptions {
  /** Stable id base — used for the help/error suffix ids. */
  id: string;
  /** Label text. */
  label: string;
  /** Optional help line under the control. */
  help?: string;
  /** Optional error line — empty string suppresses; non-empty shows. */
  error?: string;
  /** True to render an asterisk + aria-required hint on the label. */
  required?: boolean;
  /** Optional CSS class for app-specific extension. */
  className?: string;
  /** Forwarded `data-testid` on the field root. */
  testid?: string;
  /** The control itself — usually a single `<input>`/`<select>`/`<textarea>`. */
  children: Renderable;
}

export function formField(opts: FormFieldOptions): Mountable {
  const helpId = opts.help ? `${opts.id}-help` : undefined;
  const errorId = `${opts.id}-error`;
  const klass = `dl-form-field${opts.className ? ` ${opts.className}` : ''}`;

  // Bare strings are valid child slots (coerced to text nodes at append);
  // raw `document.createTextNode` `Text` nodes are no longer accepted as
  // children (ChildNode = Mountable | string | number).
  const labelChildren: (string | Mountable)[] = [opts.label];
  if (opts.required) {
    labelChildren.push(
      span({ class: 'dl-form-field-required', 'aria-hidden': 'true' }, [' *']),
    );
  }

  const blocks: Mountable[] = [
    labelEl(
      {
        for: opts.id,
        class: 'dl-form-field-label',
      },
      labelChildren,
    ),
    ...opts.children,
  ];

  if (opts.help && helpId) {
    blocks.push(
      span({ id: helpId, class: 'dl-form-field-help' }, [opts.help]),
    );
  }

  // Error slot — always rendered; CSS hides when empty. Stable id so
  // the caller's aria-describedby wiring doesn't flip on/off.
  const errorContent: string[] = opts.error ? [opts.error] : [];
  blocks.push(
    span(
      {
        id: errorId,
        class: 'dl-form-field-error',
        role: 'alert',
      },
      errorContent,
    ),
  );

  return div(
    {
      class: klass,
      'data-testid': opts.testid,
    },
    blocks,
  );
}
